import { useState } from "react"
import { useMutation, useQuery } from "@tanstack/react-query"

import { MessageSquareText, History, ChevronDown, ChevronUp, Pencil, Trash2, Lock } from "lucide-react"

import preCheckInternalQaCommentsQuery from "../../services/pre-check-internal-qa-comments"
import preCheckInternalQaCommentHistoryQuery from "../../services/pre-check-internal-qa-comment-history"
import addPreCheckInternalQaCommentMutation from "../../services/add-pre-check-internal-qa-comment"
import updatePreCheckInternalQaCommentMutation from "../../services/update-pre-check-internal-qa-comment"
import deletePreCheckInternalQaCommentMutation from "../../services/delete-pre-check-internal-qa-comment"

const MAX_COMMENT_LENGTH = 5000

const formatDateTime = (date) => {
    if (!date) return ""
    return new Date(date).toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })
}

const errorMessage = (error, fallback) => error?.response?.data?.error || fallback

const ACTION_BADGES = {
    added: { label: "Added", className: "border-green-200 bg-green-50 text-green-700" },
    edited: { label: "Edited", className: "border-blue-200 bg-blue-50 text-blue-700" },
    deleted: { label: "Deleted", className: "border-red-200 bg-red-50 text-red-700" },
}

const CommentEditor = ({ initialValue = "", submitLabel, isSaving, onSubmit, onCancel }) => {
    const [value, setValue] = useState(initialValue)
    const trimmed = value.trim()
    const isValid = !!trimmed && trimmed !== initialValue.trim()

    return (
        <div>
            <textarea
                value={value}
                onChange={(e) => setValue(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
                rows={3}
                placeholder="Write an internal QA comment..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
            />
            <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                <span className="text-xs text-gray-400">{value.length}/{MAX_COMMENT_LENGTH}</span>
                <div className="flex items-center gap-2">
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isSaving}
                            className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => {
                            if (!isValid || isSaving) return
                            onSubmit(trimmed, () => setValue(""))
                        }}
                        disabled={!isValid || isSaving}
                        className="px-3 py-1.5 rounded-lg bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        {isSaving ? "Saving..." : submitLabel}
                    </button>
                </div>
            </div>
        </div>
    )
}

const CommentItem = ({ entry, onChanged }) => {
    const [isEditing, setIsEditing] = useState(false)
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

    const updateMutation = useMutation({
        mutationFn: updatePreCheckInternalQaCommentMutation,
        onSuccess: () => {
            setIsEditing(false)
            onChanged()
        },
    })

    const deleteMutation = useMutation({
        mutationFn: deletePreCheckInternalQaCommentMutation,
        onSuccess: () => {
            setIsConfirmingDelete(false)
            onChanged()
        },
    })

    return (
        <li className="p-3 rounded-lg bg-gray-50 border border-gray-100">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                <p className="text-xs text-gray-500">
                    {entry.createdByName && <span className="font-semibold text-gray-700">{entry.createdByName}</span>}
                    {" · "}{formatDateTime(entry.createdAt)}
                    {entry.editedAt && (
                        <span title={`Edited${entry.editedByName ? ` by ${entry.editedByName}` : ""} on ${formatDateTime(entry.editedAt)}`}>
                            {" · "}<span className="italic">edited {formatDateTime(entry.editedAt)}</span>
                        </span>
                    )}
                </p>
                {entry.isOwn && !isEditing && !isConfirmingDelete && (
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => setIsEditing(true)}
                            className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                            aria-label="Edit comment"
                            title="Edit"
                        >
                            <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsConfirmingDelete(true)}
                            className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                            aria-label="Delete comment"
                            title="Delete"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}
            </div>

            {isEditing ? (
                <>
                    <CommentEditor
                        initialValue={entry.comment}
                        submitLabel="Save"
                        isSaving={updateMutation.isPending}
                        onSubmit={(comment) => updateMutation.mutate({ commentId: entry._id, comment })}
                        onCancel={() => {
                            updateMutation.reset()
                            setIsEditing(false)
                        }}
                    />
                    {updateMutation.isError && (
                        <p className="text-xs text-red-600 mt-1">{errorMessage(updateMutation.error, "Could not save. Please try again.")}</p>
                    )}
                </>
            ) : (
                <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{entry.comment}</p>
            )}

            {isConfirmingDelete && (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                    <span className="text-xs text-red-700">Delete this comment? It stays in the audit trail.</span>
                    <button
                        type="button"
                        onClick={() => deleteMutation.mutate({ commentId: entry._id })}
                        disabled={deleteMutation.isPending}
                        className="px-2.5 py-1 rounded bg-red-600 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                    >
                        {deleteMutation.isPending ? "Deleting..." : "Delete"}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            deleteMutation.reset()
                            setIsConfirmingDelete(false)
                        }}
                        disabled={deleteMutation.isPending}
                        className="px-2.5 py-1 rounded border border-gray-200 bg-white text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    {deleteMutation.isError && (
                        <span className="text-xs text-red-600">{errorMessage(deleteMutation.error, "Could not delete. Please try again.")}</span>
                    )}
                </div>
            )}
        </li>
    )
}

// Full history + audit trail (every add / edit / delete, incl. deleted comments). Mounted only when
// opened, so it is fetched on demand.
const CommentAuditTrail = ({ preCheckId, refreshKey }) => {
    const { data: entries, isFetching, isError } = useQuery({
        queryKey: ["pre-check-internal-qa-comment-history", preCheckId, refreshKey],
        queryFn: () => preCheckInternalQaCommentHistoryQuery(preCheckId),
        refetchOnWindowFocus: false,
        enabled: !!preCheckId,
    })

    if (isFetching && !entries) return <p className="text-sm text-gray-500">Loading comment history...</p>
    if (isError) return <p className="text-sm text-red-600">Could not load the comment history.</p>
    if (!entries?.length) return <p className="text-sm text-gray-500">No comment activity yet.</p>

    return (
        <ul className="space-y-2">
            {entries.map((entry) => {
                const badge = ACTION_BADGES[entry.action] || { label: entry.action, className: "border-gray-200 bg-gray-100 text-gray-700" }
                return (
                    <li key={entry._id} className="p-3 rounded-lg bg-white border border-gray-200">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-semibold ${badge.className}`}>
                                {badge.label}
                            </span>
                            <span className="text-xs text-gray-500">
                                {formatDateTime(entry.createdAt)}
                                {entry.updatedByName && <> · by <span className="font-semibold text-gray-700">{entry.updatedByName}</span></>}
                            </span>
                        </div>
                        {entry.action === "edited" && entry.previousComment != null && (
                            <div className="mb-1">
                                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Before</p>
                                <p className="text-sm text-gray-500 whitespace-pre-wrap break-words line-through decoration-gray-300">{entry.previousComment}</p>
                                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mt-1">After</p>
                            </div>
                        )}
                        <p className={`text-sm whitespace-pre-wrap break-words ${entry.action === "deleted" ? "text-gray-500" : "text-gray-800"}`}>
                            {entry.comment}
                        </p>
                    </li>
                )
            })}
        </ul>
    )
}

// "Internal QA Comments" inside the Internal Investigations section (ADMIN admin scope). Free-text
// comments any admin can add; the author can edit / delete their own. Every change is kept in the
// history + audit trail below (collapsed, loaded when opened). Internal only — never shown to the
// client and not part of the TruCheck Consumer Report.
const InternalQaComments = ({ preCheckId }) => {
    const [isAuditTrailOpen, setIsAuditTrailOpen] = useState(false)
    // Bumped after a change so an open audit trail refetches.
    const [refreshKey, setRefreshKey] = useState(0)

    const { data: comments, isFetching, isError, refetch } = useQuery({
        queryKey: ["pre-check-internal-qa-comments", preCheckId],
        queryFn: () => preCheckInternalQaCommentsQuery(preCheckId),
        refetchOnWindowFocus: false,
        enabled: !!preCheckId,
    })

    const onChanged = () => {
        refetch()
        setRefreshKey((k) => k + 1)
    }

    const addMutation = useMutation({
        mutationFn: addPreCheckInternalQaCommentMutation,
        onSuccess: onChanged,
    })

    return (
        <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
                <MessageSquareText className="w-4 h-4 text-gray-400" />
                <p className="text-sm font-semibold text-gray-700">Internal QA Comments</p>
                {comments?.length > 0 && (
                    <span className="text-xs text-gray-400">({comments.length})</span>
                )}
            </div>
            <p className="flex items-center gap-1 mb-3 text-xs text-gray-500">
                <Lock className="w-3 h-3" />
                Internal only — not shown to the client and not included in the TruCheck Consumer Report.
            </p>

            <CommentEditor
                submitLabel="Add Comment"
                isSaving={addMutation.isPending}
                onSubmit={(comment, clear) => addMutation.mutate({ preCheckId, comment }, { onSuccess: clear })}
            />
            {addMutation.isError && (
                <p className="text-xs text-red-600 mt-1">{errorMessage(addMutation.error, "Could not add the comment. Please try again.")}</p>
            )}

            <div className="mt-4">
                {isFetching && !comments ? (
                    <p className="text-sm text-gray-500">Loading comments...</p>
                ) : isError ? (
                    <p className="text-sm text-red-600">Could not load the internal QA comments.</p>
                ) : !comments?.length ? (
                    <p className="text-sm text-gray-500">No internal QA comments yet.</p>
                ) : (
                    <ul className="space-y-2">
                        {comments.map((entry) => (
                            <CommentItem key={entry._id} entry={entry} onChanged={onChanged} />
                        ))}
                    </ul>
                )}
            </div>

            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50">
                <button
                    type="button"
                    onClick={() => setIsAuditTrailOpen((v) => !v)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
                >
                    <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <History className="w-4 h-4 text-gray-400" />
                        Comment History &amp; Audit Trail
                    </span>
                    {isAuditTrailOpen ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                </button>
                {isAuditTrailOpen && (
                    <div className="px-3 pb-3">
                        <CommentAuditTrail preCheckId={preCheckId} refreshKey={refreshKey} />
                    </div>
                )}
            </div>
        </div>
    )
}

export default InternalQaComments
